
import React, { useState, useRef } from 'react';
import { ArrowLeft, Upload, Sparkles, Loader2, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { chatApi, TEXT_MODEL, VISION_MODEL } from '../lib/api';
import { TRAINER_PROMPT } from '../lib/prompts';

interface TrainerProps {
  onBack: () => void;
}

interface AnalysisOption {
  god: string;
  door: string;
  star: string;
  heavenStem: string;
  earthStem: string;
  reasoning: string;
}

interface TrainerResult {
  options: AnalysisOption[];
}

const ALLOWED_STEMS = ['乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];

const REFUSAL_PATTERNS = [
  '无法',
  '不能',
  '不适用',
  '无关',
  '不相关',
  '无法映射',
  '奇门遁甲是一种古代',
];

function looksLikeRefusal(text: string): boolean {
  const t = String(text || '').toLowerCase();
  if (!t) return false;
  return REFUSAL_PATTERNS.some((p) => t.includes(p.toLowerCase()));
}

function extractJsonText(raw: string): string {
  const text = String(raw || '')
    .replace(/^\uFEFF/, '')
    .replace(/^json\s*/i, '')
    .trim();
  if (!text) return text;
  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (codeBlockMatch?.[1]) return codeBlockMatch[1].trim();
  const start = text.indexOf('{');
  if (start >= 0) {
    let depth = 0;
    let inStr = false;
    let escaped = false;
    for (let i = start; i < text.length; i += 1) {
      const ch = text[i];
      if (inStr) {
        if (escaped) {
          escaped = false;
          continue;
        }
        if (ch === '\\') {
          escaped = true;
          continue;
        }
        if (ch === '"') inStr = false;
        continue;
      }
      if (ch === '"') {
        inStr = true;
        continue;
      }
      if (ch === '{') depth += 1;
      if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          return text.slice(start, i + 1).trim();
        }
      }
    }
  }
  const end = text.lastIndexOf('}');
  if (start >= 0 && end > start) return text.slice(start, end + 1).trim();
  return text;
}

function normalizeOption(item: unknown): AnalysisOption {
  const obj = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
  const pickStem = (v: unknown) => {
    const text = String(v ?? '').trim();
    const hit = ALLOWED_STEMS.find((stem) => text.includes(stem));
    return hit || '乙';
  };
  return {
    god: String(obj.god ?? obj.deity ?? obj['八神'] ?? '').trim(),
    door: String(obj.door ?? obj['八门'] ?? '').trim(),
    star: String(obj.star ?? obj['九星'] ?? '').trim(),
    heavenStem: pickStem(obj.heavenStem ?? obj.tianPan ?? obj['天盘干'] ?? ''),
    earthStem: pickStem(obj.earthStem ?? obj.diPan ?? obj['地盘干'] ?? ''),
    reasoning: String(obj.reasoning ?? obj.analysis ?? obj['推导逻辑'] ?? obj['说明'] ?? '').trim(),
  };
}

function parseTrainerResult(raw: string): TrainerResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(extractJsonText(raw));
  } catch {
    throw new Error('解析结果失败，请重试');
  }

  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(extractJsonText(parsed));
    } catch {
      throw new Error('解析结果失败，请重试');
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('返回格式异常，请重试');
  }

  const source = parsed as { options?: unknown; error?: unknown };
  if (typeof source.error === 'string' && source.error) {
    throw new Error(source.error);
  }
  if (!Array.isArray(source.options) || source.options.length === 0) {
    throw new Error('未返回可用推演结果，请重试');
  }

  const options: AnalysisOption[] = source.options.map(normalizeOption).filter((o) => {
    const filled = [o.god, o.door, o.star, o.heavenStem, o.earthStem, o.reasoning].filter(Boolean).length;
    return filled >= 3;
  });
  if (options.length === 0) {
    throw new Error('未返回有效推演内容，请更换图片或补充文字描述');
  }

  return { options };
}

const parseInline = (text: string) => {
  const parts = text.split(/(==.*?==|\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('==') && part.endsWith('==')) {
      return <span key={i} className="bg-amber-100 text-amber-900 px-1 rounded mx-0.5">{part.slice(2, -2)}</span>;
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <span key={i} className="font-bold text-slate-900">{part.slice(2, -2)}</span>;
    }
    return part;
  });
};

const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n').filter(line => line.trim() !== '');
  return (
    <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('#### ')) {
           return <h4 key={index} className="text-base font-bold text-slate-700 mt-4 mb-2">{parseInline(trimmed.slice(5))}</h4>;
        }
        if (trimmed.startsWith('### ')) {
           return <h3 key={index} className="text-lg font-bold text-slate-800 mt-4 mb-2">{parseInline(trimmed.slice(4))}</h3>;
        }
        if (trimmed.startsWith('## ') || trimmed.startsWith('【')) {
           return <h2 key={index} className="text-xl font-bold text-slate-900 mt-4 mb-2">{parseInline(trimmed.replace(/^[#【]+|[】]+$/g, ''))}</h2>;
        }
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
           return (
             <div key={index} className="flex items-start gap-2 pl-2">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></div>
                <div>{parseInline(trimmed.substring(2))}</div>
             </div>
           );
        }
        return <p key={index}>{parseInline(line)}</p>;
      })}
    </div>
  );
};

const loadImageDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('读取图片失败'));
  reader.onload = () => resolve(String(reader.result || ''));
  reader.readAsDataURL(file);
});

const compressImage = async (dataUrl: string, maxSize = 1024, quality = 0.72) => {
  const img = new Image();
  const loaded = new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('图片解析失败'));
  });
  img.src = dataUrl;
  await loaded;

  const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
};

const Trainer: React.FC<TrainerProps> = ({ onBack }) => {
  const [inputText, setInputText] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TrainerResult | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fixToJsonOnce = async (rawText: string): Promise<string> => {
    const repairPrompt = `请把下面内容整理为严格 JSON，仅输出 JSON 对象，不要 Markdown。
目标格式：{"options":[{"god":"八神","door":"八门","star":"九星","heavenStem":"天盘干","earthStem":"地盘干","reasoning":"推导逻辑"}]}
硬性约束：heavenStem 和 earthStem 只能是 乙/丙/丁/戊/己/庚/辛/壬/癸，不得出现甲。
原始内容：
${rawText}`;
    const fixed = await chatApi({
      messages: [{ role: 'user', content: repairPrompt }],
      model: TEXT_MODEL,
      jsonMode: true,
      feature: 'trainer',
    });
    return fixed.text || '';
  };

  const requestForcedSymbolicMapping = async (
    content: string | ({ type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } })[],
    userDesc: string
  ): Promise<string> => {
    const forcedPrompt = `你是一位“跨域奇门象意映射专家”。任务：无论图片属于时尚、生活、人物、宠物、食物、建筑、运动还是抽象画，都必须完成象意映射，不得以“无关/无法分析”拒答。

规则：
1. 先从可见要素提取：颜色、材质、形状、姿态、环境、冷暖、动静、明暗。
2. 将这些要素映射到：五行倾向、八门气质、九星特征、八神性格，再组合成2套不同推演。
3. 禁止输出“奇门与该图片无关、无法推演、不能分析”之类语句。
4. 不做道德评判，不涉及现实建议，只做象意解释。
5. 天盘干/地盘干只能从 乙、丙、丁、戊、己、庚、辛、壬、癸 中选择，严禁出现甲。
6. 仅输出 JSON，不要 Markdown。

输出格式：
{"options":[{"god":"八神","door":"八门","star":"九星","heavenStem":"天盘干","earthStem":"地盘干","reasoning":"150字以上，明确说明由哪些视觉要素映射得到"}]}

用户补充描述：${userDesc || '无'}`;

    const forced = await chatApi({
      messages: [{ role: 'user', content: Array.isArray(content) ? [{ type: 'text', text: forcedPrompt }, ...content.filter((c) => c.type === 'image_url')] : `${forcedPrompt}\n${content}` }],
      model: VISION_MODEL,
      jsonMode: false,
      feature: 'trainer',
    });
    return forced.text || '';
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const raw = await loadImageDataUrl(file);
        const compressed = await compressImage(raw);
        setSelectedImage(compressed || raw);
      } catch {
        setSelectedImage(null);
      }
    }
  };

  const handleAnalyze = async () => {
    if (!inputText && !selectedImage) return;
    setIsLoading(true);
    setResult(null);

    try {
      const prompt = `${TRAINER_PROMPT}
      
      用户输入内容: ${inputText || '无额外描述'}
      `;
      const textPart = prompt;
      const content: string | ({ type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } })[] = selectedImage
        ? [
            { type: 'text', text: textPart },
            { type: 'image_url', image_url: { url: selectedImage } },
          ]
        : textPart;

      const { text } = await chatApi({
        messages: [{ role: 'user', content }],
        model: VISION_MODEL,
        jsonMode: false,
        feature: 'trainer',
      });

      if (!text) {
        alert('分析失败，请重试');
        return;
      }
      let parsed: TrainerResult;
      try {
        if (looksLikeRefusal(text)) {
          throw new Error('refusal');
        }
        parsed = parseTrainerResult(text);
      } catch {
        let secondText = await fixToJsonOnce(text);
        if (!secondText || looksLikeRefusal(secondText)) {
          secondText = await requestForcedSymbolicMapping(content, inputText);
        }
        parsed = parseTrainerResult(secondText);
      }
      setResult(parsed);
      setActiveTab(0);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '分析失败，请重试。';
      alert(msg === '会员已过期' ? '会员已过期，请重新激活。' : msg);
    } finally {
      setIsLoading(false);
    }
  };

  const hasResult = !!result && Array.isArray(result.options) && result.options.length > 0;
  const activeOption = hasResult ? result.options[Math.min(activeTab, result.options.length - 1)] : null;

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="bg-slate-900 text-amber-50 p-4 shadow-md flex items-center justify-between shrink-0">
        <div className="flex items-center">
          <button onClick={onBack} className="p-2 hover:bg-slate-700 rounded-full mr-4"><ArrowLeft className="w-5 h-5"/></button>
          <h1 className="text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-400" />图文转象意</h1>
        </div>
        {result && <button onClick={() => { setResult(null); setInputText(''); setSelectedImage(null); }} className="px-4 py-2 bg-slate-800 rounded-full text-sm font-bold flex items-center gap-2"><RefreshCw className="w-4 h-4"/>重置</button>}
      </div>

      <div className="flex-1 overflow-y-auto p-4 lg:p-8">
        {!hasResult ? (
          <div className="max-w-4xl mx-auto grid lg:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 space-y-4 shadow-sm">
              <h2 className="font-bold text-slate-700 flex items-center gap-2"><Upload className="w-4 h-4"/>上传素材</h2>
              <textarea value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder="描述场景..." className="w-full p-3 bg-slate-50 border rounded-lg h-24 text-sm" />
              <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed rounded-xl h-32 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-all overflow-hidden bg-slate-50">
                {selectedImage ? <img src={selectedImage} className="h-full w-full object-cover" /> : <><ImageIcon className="text-slate-300 mb-1"/><span className="text-xs text-slate-400">选择图片</span></>}
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleImageUpload} />
              </div>
              <button onClick={handleAnalyze} disabled={isLoading} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-md hover:bg-indigo-700 disabled:bg-slate-300 flex justify-center items-center gap-2">
                {isLoading ? <Loader2 className="animate-spin w-5 h-5"/> : <Sparkles className="w-5 h-5"/>} {isLoading ? "计算中" : "开始推演"}
              </button>
            </div>
            <div className="hidden lg:flex border-2 border-dashed rounded-2xl items-center justify-center text-slate-300">结果预览区</div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="flex gap-4 border-b">
              {result!.options.map((_, i) => (
                <button key={i} onClick={() => setActiveTab(i)} className={`pb-2 px-4 font-bold text-sm ${activeTab === i ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-400'}`}>推演 {i+1}</button>
              ))}
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
               <div className="bg-white p-6 rounded-2xl border shadow-sm grid grid-cols-2 gap-4">
                  <div className="bg-rose-50 p-4 rounded-xl text-center"><div className="text-[10px] text-rose-400 font-bold">八神</div><div className="text-xl font-bold text-rose-700">{activeOption?.god || '-'}</div></div>
                  <div className="bg-amber-50 p-4 rounded-xl text-center"><div className="text-[10px] text-amber-400 font-bold">九星</div><div className="text-xl font-bold text-amber-700">{activeOption?.star || '-'}</div></div>
                  <div className="bg-emerald-50 p-4 rounded-xl text-center col-span-2"><div className="text-[10px] text-emerald-400 font-bold">八门</div><div className="text-2xl font-bold text-emerald-700">{activeOption?.door || '-'}</div></div>
                  <div className="bg-indigo-50 p-4 rounded-xl text-center"><div className="text-[10px] text-indigo-400 font-bold">天盘</div><div className="text-xl font-bold text-indigo-700">{activeOption?.heavenStem || '-'}</div></div>
                  <div className="bg-slate-50 p-4 rounded-xl text-center"><div className="text-[10px] text-slate-400 font-bold">地盘</div><div className="text-xl font-bold text-slate-700">{activeOption?.earthStem || '-'}</div></div>
               </div>
               <div className="bg-white p-6 rounded-2xl border shadow-sm"><MarkdownRenderer content={activeOption?.reasoning || ''}/></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Trainer;
